import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { t, type StringKey } from '../i18n'
import { isTeacher } from '../useClassroomSignals'
import { ACCEPT_FILES_ATTRIBUTE, formatBytes, isSupportedChatFile } from './attachment'
import type { LinkPreview } from './chatApi'
import { dragHasFiles, readDrop, readPaste } from './dropzone'
import { isAnimatedImage, isSupportedImage } from './image'
import { firstLink, isSafePreviewImage, splitLinks } from './linkify'
import { classifyMedia, type LinkedMedia } from './media'
import type { ChatApi } from './useChat'
import { attachmentUrl, CHAT_QUEUE_CAP, isFileAttachment, type ChatMessage } from './types'

/**
 * The chat sheet — the third absolute sibling in `.classroom`, following every
 * rule the roster and the background picker established: overlays the lesson
 * with zero video reflow, no backdrop-filter, entrance settles on
 * `transform: none`, tap-outside dismisses, Escape returns focus.
 *
 * Rendering shape (chat plan v2 + Grok round-2):
 * - This component SUBSCRIBES to the chat store (useSyncExternalStore); message
 *   traffic re-renders this panel and the control-bar badge, never Classroom or
 *   the video tiles.
 * - A WINDOW renders, never the ring: the last 40 messages, "show earlier"
 *   pages by 20 with scroll-position compensation. 200 bubbles never mount.
 * - Bubbles are memoized: a commit that appends two messages re-renders two
 *   bubbles, not forty. Rows are REPLACED (never mutated) by the store exactly
 *   so this comparison is safe.
 * - Scroll anchoring: appends only follow the bottom if the reader is at the
 *   bottom; otherwise a quiet "new messages" chip.
 */

const WINDOW_DEFAULT = 40
const WINDOW_STEP = 20
/** "At the bottom" within this many px still counts — rubber-banding is not scrolling away. */
const BOTTOM_SLACK = 48

/**
 * Every way an image can be refused, mapped to the sentence the person reads.
 * A table rather than a nested ternary because the set grew: each entry is a
 * promise that this failure has its own words, and the fallback is the only
 * vague one.
 */
const ATTACHMENT_REFUSAL_KEY: Record<string, StringKey> = {
  busy: 'chat.imgBusy',
  frozen: 'chat.frozenStudent',
  muted: 'chat.mutedYou',
  tooLarge: 'chat.imgTooLarge',
  notImage: 'chat.imgNotImage',
  animated: 'chat.imgAnimated',
  timedOut: 'chat.imgTimedOut',
  decodeFailed: 'chat.imgUnreadable',
  notAllowed: 'chat.observer',
  fileTooLarge: 'chat.fileTooLarge',
  notFile: 'chat.notFile',
}

interface Props {
  open: boolean
  onClose: () => void
  chat: ChatApi
  isHost: boolean
  /**
   * False for hidden staff observers. Their token carries canPublishData:false
   * and the API refuses their writes, so an enabled composer produced a message
   * that appeared to send — with a local echo only they could see. They read
   * the class chat; they do not speak in it.
   */
  canSend: boolean
  myIdentity: string
  returnFocusTo?: React.RefObject<HTMLButtonElement>
}

export function ChatPanel({ open, onClose, chat, isHost, canSend, myIdentity, returnFocusTo }: Props) {
  const snap = useSyncExternalStore(chat.subscribe, chat.getSnapshot)
  const panelRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const closeHandler = useRef(onClose)
  closeHandler.current = onClose
  const restoreFocus = useRef(true)

  const [windowSize, setWindowSize] = useState(WINDOW_DEFAULT)
  /** scrollHeight before an "earlier" page-in, so the view can hold its place. */
  const pageAnchorRef = useRef<number | null>(null)
  const [newBelow, setNewBelow] = useState(false)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const atBottomRef = useRef(true)
  const [dragOver, setDragOver] = useState(false)
  /**
   * `dragenter`/`dragleave` fire on every CHILD boundary, not just the panel's —
   * dragging across a bubble leaves and re-enters, so a naive boolean strobes
   * the drop target off and on under the tutor's hand. Depth counting is the
   * only reading of these events that is actually about the panel.
   */
  const dragDepthRef = useRef(0)
  /**
   * A drag is not a click. The outside-`pointerdown` dismissal below would
   * otherwise close the panel the instant a drag began outside it — unmounting
   * the drop target before `drop` could ever be dispatched to it, so the file
   * silently went nowhere.
   */
  const draggingRef = useRef(false)
  const [search, setSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  /**
   * Latest-value refs for the drop and paste handlers. Both are reached from
   * listeners that outlive the render which created them, and both must gate on
   * the freeze/mute state as it is AT THE DROP — not as it was when the tutor
   * picked the file up.
   */
  const composerDisabledRef = useRef(false)
  const snapRef = useRef(snap)
  snapRef.current = snap

  // Dismissal contract shared with Roster/BackgroundPicker.
  useEffect(() => {
    if (!open) return
    restoreFocus.current = true
    inputRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Innermost layer first: with the lightbox up, Escape means "back to
        // the chat", not "close the chat". (Ref, not state — this listener
        // outlives renders.)
        if (lightboxRef.current) {
          setLightbox(null)
          return
        }
        closeHandler.current()
      }
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target)) return
      if (returnFocusTo?.current?.contains(target)) return
      // Mid-drag this is the mouse-down that STARTED the drag, somewhere else on
      // screen. Closing here would take the drop target away before the drop.
      if (draggingRef.current) return
      restoreFocus.current = false
      closeHandler.current()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
      if (restoreFocus.current) returnFocusTo?.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref identity is stable
  }, [open])

  /**
   * The navigation guard, and the reason it is NOT scoped to `open`.
   *
   * A file dropped on a page where nothing accepts it makes the browser NAVIGATE
   * TO THE FILE — the whole classroom replaced by a photo, mid-lesson, camera
   * and microphone gone, rejoining by hand. A tutor reaching for the chat and
   * missing by twenty pixels is enough to trigger it. The panel is unmounted for
   * most of a lesson, so a guard that only ran while it was open would be absent
   * for exactly the aim that misses.
   *
   * `drop` is prevented ONLY when nothing inner already claimed it. No sibling
   * takes file drops today — the whiteboard runs with Excalidraw's image tool
   * off (Whiteboard.tsx) — so this currently costs nothing; it is written this
   * way so that the first component which DOES want a drop simply works, rather
   * than losing its default to a guard it cannot see.
   */
  useEffect(() => {
    const onDragOver = (event: DragEvent) => {
      if (dragHasFiles(event.dataTransfer)) event.preventDefault()
    }
    const onDrop = (event: DragEvent) => {
      draggingRef.current = false
      dragDepthRef.current = 0
      setDragOver(false)
      if (!event.defaultPrevented) event.preventDefault()
    }
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('drop', onDrop)
    }
  }, [])

  /**
   * A drag anywhere arms the dismissal suppression; ending it disarms.
   *
   * `dragend` alone is not enough to disarm. It fires on the drag SOURCE, and a
   * file dragged in from the desktop has no source in this document — so a drag
   * that wandered out of the window again would leave the flag stuck on and the
   * panel would stop closing on outside taps for the rest of the lesson.
   * `dragleave` with a null `relatedTarget` is the pointer leaving the document,
   * which is the case `dragend` misses.
   */
  useEffect(() => {
    const arm = (event: DragEvent) => {
      if (dragHasFiles(event.dataTransfer)) draggingRef.current = true
    }
    /**
     * Disarming has to clear the OVERLAY too, not just the suppression flag.
     * A drag abandoned with Escape, or dragged back out of the window, never
     * reaches the panel's own `dragleave` — so the depth counter keeps whatever
     * it had and the "drop pictures here" sheet stays over the conversation for
     * the rest of the lesson, with no gesture left that would take it down.
     */
    const disarm = () => {
      draggingRef.current = false
      dragDepthRef.current = 0
      setDragOver(false)
    }
    const onLeaveDocument = (event: DragEvent) => {
      if (event.relatedTarget === null) disarm()
    }
    window.addEventListener('dragenter', arm)
    window.addEventListener('dragend', disarm)
    window.addEventListener('dragleave', onLeaveDocument)
    return () => {
      window.removeEventListener('dragenter', arm)
      window.removeEventListener('dragend', disarm)
      window.removeEventListener('dragleave', onLeaveDocument)
    }
  }, [])

  // Fresh open: reset the window and land at the bottom. A LAYOUT effect, and
  // it scrolls here rather than leaving it to the append effect: the remounted
  // list starts at scrollTop 0 and the append effect runs first, sees the
  // stale atBottomRef from the previous session, and never scrolls — so
  // reopening after reading history stranded you at the top.
  /** Last foreignSeq acted on — the chip fires only when this advances. */
  const seenForeignRef = useRef(0)

  useLayoutEffect(() => {
    if (!open) return
    setWindowSize(WINDOW_DEFAULT)
    setNewBelow(false)
    atBottomRef.current = true
    pageAnchorRef.current = null
    seenForeignRef.current = snap.foreignSeq
    const list = listRef.current
    if (list) list.scrollTop = list.scrollHeight
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset consumes the open-instant snapshot only
  }, [open])

  const onScroll = useCallback(() => {
    const list = listRef.current
    if (!list) return
    const gap = list.scrollHeight - list.scrollTop - list.clientHeight
    atBottomRef.current = gap < BOTTOM_SLACK
    if (atBottomRef.current) setNewBelow(false)
  }, [])

  // Older rows paged in above the viewport: shift scrollTop by exactly the
  // height they added, so the message under the reader's thumb stays put.
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || pageAnchorRef.current === null) return
    list.scrollTop += list.scrollHeight - pageAnchorRef.current
    pageAnchorRef.current = null
  }, [windowSize])

  // Anchoring: follow appends only when already at the bottom. The chip keys
  // off foreignSeq, not messages identity — upload upgrades and tombstones
  // also replace the array, and they are not news to a reader scrolled up.
  useLayoutEffect(() => {
    if (!open) return
    const list = listRef.current
    if (!list) return
    const hasNews = snap.foreignSeq !== seenForeignRef.current
    seenForeignRef.current = snap.foreignSeq
    if (pageAnchorRef.current !== null) {
      // Paging owns the scroll this frame, but news arriving mid-page-in must
      // not be swallowed with it.
      if (hasNews && !atBottomRef.current) setNewBelow(true)
      return
    }
    if (atBottomRef.current) {
      list.scrollTop = list.scrollHeight
      setNewBelow(false)
    } else if (hasNews) {
      setNewBelow(true)
    }
  }, [snap.messages, snap.foreignSeq, open])

  // Changing the search term replaces the list under the reader. Whatever
  // scrollTop meant a moment ago means nothing now, and the most recent matches
  // are the ones a tutor mid-lesson is looking for.
  useLayoutEffect(() => {
    if (!open) return
    const list = listRef.current
    if (!list) return
    setWindowSize(WINDOW_DEFAULT)
    list.scrollTop = list.scrollHeight
    atBottomRef.current = true
    setNewBelow(false)
  }, [search, open])

  const jumpToLatest = useCallback(() => {
    const list = listRef.current
    if (!list) return
    atBottomRef.current = true
    list.scrollTop = list.scrollHeight
    setNewBelow(false)
  }, [])

  const submit = useCallback(() => {
    const input = inputRef.current
    if (!input) return
    if (chat.sendMessage(input.value)) {
      input.value = ''
      input.style.height = 'auto'
      atBottomRef.current = true
    }
  }, [chat])

  const [lightbox, setLightbox] = useState<string | null>(null)
  const lightboxRef = useRef<string | null>(null)
  lightboxRef.current = lightbox
  /** Set when a gesture is refused before the store ever sees it. */
  const [localNotice, setLocalNotice] = useState<string | null>(null)
  const noticeTimerRef = useRef<number | undefined>(undefined)
  /** Notices expire, like the store's refusals — a line that never leaves reads
   *  as a chat that is still refusing, long after the drop it described. */
  const showNotice = useCallback((message: string | null) => {
    setLocalNotice(message)
    window.clearTimeout(noticeTimerRef.current)
    if (message) noticeTimerRef.current = window.setTimeout(() => setLocalNotice(null), 6000)
  }, [])
  useEffect(() => () => window.clearTimeout(noticeTimerRef.current), [])
  const fileRef = useRef<HTMLInputElement>(null)
  const uploading = snap.pending > 0

  /**
   * Whatever is in the composer when a picture is chosen becomes its CAPTION.
   * The wire has always carried body and attachment together and the server has
   * always stored both — only the client insisted on sending images mute.
   */
  /**
   * WHY the composer is dead, in the order the reasons actually apply. A hidden
   * staff observer is not frozen and not muted — they simply do not speak in the
   * class chat — and telling them "the chat is paused for now" describes a
   * temporary state that will never end for them.
   */
  const refusalForDisabledComposer = useCallback((): 'muted' | 'frozen' | 'notAllowed' => {
    if (!canSend) return 'notAllowed'
    if (snapRef.current.myChatMuted) return 'muted'
    return 'frozen'
  }, [canSend])

  const readCaption = useCallback((): string => inputRef.current?.value.trim() ?? '', [])

  /** Cleared only once an image has actually been taken — a drop refused for a
   *  full queue must not also eat the sentence the tutor had already typed. */
  const clearCaption = useCallback(() => {
    const input = inputRef.current
    if (!input || !input.value) return
    input.value = ''
    input.style.height = 'auto'
  }, [])

  /**
   * The ONE place a file is judged. The picker, the drop target and the
   * clipboard all arrive here with whatever they carried, so a file offered by
   * one route can never be refused by another.
   */
  const acceptFiles = useCallback(
    (candidates: File[]) => {
      showNotice(null)
      // The OS file dialog is open for seconds, and a freeze can land inside
      // that gap. Without this the caption is consumed and cleared, and then
      // every file is refused one by one — the picture does not send and the
      // sentence explaining it is gone too. Drop and paste gate at the gesture;
      // the picker is the only path where the gesture and the files are far
      // enough apart in time for the rules to have changed between them.
      if (composerDisabledRef.current) {
        chat.reportImageRefusal(refusalForDisabledComposer())
        return
      }
      atBottomRef.current = true
      // Pictures AND allowlisted files (LK-009) — the pump judges each file by
      // kind, so one drop can carry both.
      const files = candidates.filter((file) => isSupportedImage(file) || isSupportedChatFile(file))
      if (files.length === 0) {
        // Name the animated case specifically. "That is not a picture" about a
        // GIF is true, useless, and reads as a bug.
        chat.reportImageRefusal(candidates.some(isAnimatedImage) ? 'animated' : 'notImage')
        return
      }
      const { accepted, dropped, captionRejected } = chat.queueAttachments(files, readCaption())
      // Only clear what was actually taken. A caption refused for length stays
      // in the box so it can be shortened, rather than vanishing with a
      // "too long" message and nothing left to shorten.
      if (accepted > 0 && !captionRejected) clearCaption()
      if (dropped > 0) {
        showNotice(t('chat.dropTooMany').replace('{count}', String(CHAT_QUEUE_CAP)))
      } else if (files.length !== candidates.length) {
        chat.reportImageRefusal(candidates.some(isAnimatedImage) ? 'animated' : 'notImage')
      }
    },
    [chat, readCaption, clearCaption, showNotice, refusalForDisabledComposer],
  )

  // Open a file attachment (LK-009): mint the short-lived private URL from the
  // API, then open it OUT of the app. The presign round-trip is async, so a
  // browser that has already dropped the click's transient activation may
  // block the popup — that reads as "nothing happened", so it gets words.
  const openFile = useCallback(
    (id: string) => {
      showNotice(null)
      void chat.fileUrl(id).then((url) => {
        if (!url || !window.open(url, '_blank', 'noopener,noreferrer')) {
          showNotice(t('chat.fileOpenFailed'))
        }
      })
    },
    [chat, showNotice],
  )

  // Tap a failed image bubble: re-run the upload with the SAME id and the blobs
  // already processed — no second decode, no new shell in the list.
  const retryImage = useCallback((id: string) => {
    showNotice(null)
    atBottomRef.current = true
    void chat.retryImage(id)
  }, [chat, showNotice])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      dragDepthRef.current = 0
      draggingRef.current = false
      setDragOver(false)
      if (composerDisabledRef.current) {
        chat.reportImageRefusal(refusalForDisabledComposer())
        return
      }
      const reading = readDrop(event.dataTransfer)
      if (reading.kind === 'files') {
        acceptFiles(reading.files)
        return
      }
      if (reading.kind === 'url') {
        // An image dragged from a WEB PAGE is a URL, not a file. We cannot fetch
        // it — `connect-src` forbids it, and aiming a child's browser at an
        // arbitrary host is the thing `isSafePreviewImage` exists to refuse. So
        // it becomes what it honestly is: a link in the composer.
        const input = inputRef.current
        if (!input) return
        input.value = input.value ? `${input.value} ${reading.url}` : reading.url
        input.focus()
      }
    },
    [acceptFiles, chat, refusalForDisabledComposer],
  )

  const onPaste = useCallback(
    (event: React.ClipboardEvent) => {
      const reading = readPaste(event.clipboardData)
      // Only a picture is intercepted. Hijacking a text paste would break the
      // one thing the composer is actually for.
      if (reading.kind !== 'files') return
      event.preventDefault()
      if (composerDisabledRef.current) {
        chat.reportImageRefusal(refusalForDisabledComposer())
        return
      }
      acceptFiles(reading.files)
    },
    [acceptFiles, chat, refusalForDisabledComposer],
  )

  // Stable per-panel handlers so memoized bubbles never re-render for identity
  // churn — they take the id/identity as arguments instead of closing over rows.
  const deleteMessage = useCallback(
    (id: string) => void chat.moderate('delete', { messageId: id }),
    [chat],
  )
  const toggleMute = useCallback(
    (identity: string, muted: boolean) =>
      void chat.moderate(muted ? 'unmute' : 'mute', { identity }),
    [chat],
  )

  if (!open) return null

  const composerDisabled = !canSend || (!isHost && snap.frozen) || snap.myChatMuted
  composerDisabledRef.current = composerDisabled

  // A search narrows the WHOLE ring, not the rendered window — the point of
  // searching is to reach the message that scrolled out of it. The window still
  // caps what mounts, so a term matching everything cannot mount 200 bubbles.
  const term = search.trim().toLowerCase()
  const pool = term
    ? snap.messages.filter(
      (message) =>
        !message.deleted
        && ((message.body ?? '').toLowerCase().includes(term)
          || message.name.toLowerCase().includes(term)),
    )
    : snap.messages
  const visible = pool.slice(-windowSize)
  const hasEarlier = pool.length > windowSize

  return (
    <div
      className={`chatpanel ${dragOver ? 'chatpanel--drop' : ''}`}
      ref={panelRef}
      role="dialog"
      aria-label={t('chat.title')}
      aria-modal="false"
      onDragEnter={(event) => {
        if (!dragHasFiles(event.dataTransfer)) return
        event.preventDefault()
        dragDepthRef.current += 1
        draggingRef.current = true
        setDragOver(true)
      }}
      onDragOver={(event) => {
        if (!dragHasFiles(event.dataTransfer)) return
        // Without this the element is not a drop target at all and `drop` never
        // fires — the single easiest way to build a drop zone that does nothing.
        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'
      }}
      onDragLeave={() => {
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
        if (dragDepthRef.current === 0) setDragOver(false)
      }}
      onDrop={onDrop}
    >
      {dragOver ? (
        <div className="chatpanel__drop" aria-hidden="true">
          <ImageIcon />
          <strong>{t('chat.drop')}</strong>
          <span>{t('chat.dropHint')}</span>
        </div>
      ) : null}
      <header className="chatpanel__head">
        <h2 className="chatpanel__title">{t('chat.title')}</h2>
        <button
          type="button"
          className={`chatpanel__icon ${searchOpen ? 'chatpanel__icon--on' : ''}`}
          onClick={() => {
            setSearchOpen((value) => {
              if (value) setSearch('')
              return !value
            })
          }}
          aria-pressed={searchOpen}
          aria-label={t('chat.search')}
          title={t('chat.search')}
        >
          <SearchIcon />
        </button>
        {isHost ? (
          <button
            type="button"
            className={`chatpanel__freeze ${snap.frozen ? 'chatpanel__freeze--on' : ''}`}
            onClick={() => void chat.moderate(snap.frozen ? 'unfreeze' : 'freeze')}
            aria-pressed={snap.frozen}
          >
            <span aria-hidden="true">❄</span> {t(snap.frozen ? 'chat.unfreeze' : 'chat.freeze')}
          </button>
        ) : null}
        <button
          type="button"
          className="chatpanel__close"
          onClick={onClose}
          aria-label={t('roster.close')}
          title={t('roster.close')}
        >
          <CloseIcon />
        </button>
      </header>

      {/* Standing state banners: quiet, but always explain a dead composer. */}
      {snap.frozen ? (
        <p className="chatpanel__banner" role="status">
          {t(isHost ? 'chat.frozenHost' : 'chat.frozenStudent')}
        </p>
      ) : null}
      {snap.myChatMuted ? (
        <p className="chatpanel__banner chatpanel__banner--muted" role="status">
          {t('chat.mutedYou')}
        </p>
      ) : null}

      {searchOpen ? (
        <div className="chatpanel__search">
          <input
            type="search"
            className="chatpanel__searchinput"
            dir="auto"
            autoFocus
            value={search}
            placeholder={t('chat.searchPlaceholder')}
            aria-label={t('chat.search')}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              // Escape clears before it closes: one key, two useful outcomes,
              // and it never reaches the panel-wide handler that would dismiss
              // the whole sheet mid-search.
              if (event.key !== 'Escape') return
              event.stopPropagation()
              if (search) setSearch('')
              else setSearchOpen(false)
            }}
          />
          {term ? (
            <span className="chatpanel__searchcount">
              {t('chat.searchCount').replace('{count}', String(pool.length))}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="chatpanel__list" ref={listRef} onScroll={onScroll}>
        {hasEarlier ? (
          <button
            type="button"
            className="chatpanel__earlier"
            onClick={() => {
              pageAnchorRef.current = listRef.current?.scrollHeight ?? null
              setWindowSize((size) => size + WINDOW_STEP)
            }}
          >
            {t('chat.earlier')}
          </button>
        ) : null}

        {visible.length === 0 ? (
          <p className="chatpanel__empty">
            {term ? t('chat.searchNone').replace('{term}', search.trim()) : t('chat.empty')}
          </p>
        ) : null}

        {visible.map((message, index) => (
          <Bubble
            key={message.id}
            message={message}
            mine={message.identity === myIdentity}
            showName={index === 0 || visible[index - 1].identity !== message.identity}
            canModerate={isHost}
            muted={snap.state.mutes.includes(message.identity)}
            onDelete={deleteMessage}
            onToggleMute={toggleMute}
            onOpenImage={setLightbox}
            onOpenFile={openFile}
            onRetryImage={retryImage}
            unfurl={chat.unfurl}
          />
        ))}
      </div>

      {newBelow ? (
        <button type="button" className="chatpanel__new" onClick={jumpToLatest}>
          ↓ {t('chat.new')}
        </button>
      ) : null}

      {/* The composer's file chip (LK-009): a non-image attachment being sent
          has a name and a size worth showing while the PUT runs. */}
      {snap.pendingFile ? (
        <div className="chatpanel__pendingfile" role="status">
          <span aria-hidden="true"><FileIcon /></span>
          <bdi dir="auto">{snap.pendingFile.name}</bdi>
          <span dir="ltr">{formatBytes(snap.pendingFile.bytes)}</span>
        </div>
      ) : null}
      <footer className="chatpanel__composer">
        <button
          type="button"
          className="chatpanel__attach"
          disabled={composerDisabled || uploading}
          onClick={() => fileRef.current?.click()}
          aria-label={t('chat.attach')}
          title={t('chat.attach')}
        >
          <ImageIcon />
        </button>
        <input
          ref={fileRef}
          type="file"
          // The exact list the pipeline accepts, not `image/*`. The wildcard let
          // the dialog offer a GIF and then answered "that is not a picture" —
          // a file the tutor had just been shown, refused in the least helpful
          // true words available.
          accept={ACCEPT_FILES_ATTRIBUTE}
          multiple
          className="visually-hidden"
          onChange={(event) => {
            const picked = Array.from(event.target.files ?? [])
            if (picked.length > 0) acceptFiles(picked)
            event.target.value = '' // let the same file be picked again after a fail
          }}
        />
        <button
          type="button"
          className="chatpanel__emoji-btn"
          disabled={composerDisabled}
          onClick={() => setEmojiOpen((v) => !v)}
          aria-expanded={emojiOpen}
          aria-label={t('chat.emoji')}
          title={t('chat.emoji')}
        >
          😊
        </button>
        <textarea
          ref={inputRef}
          className="chatpanel__input"
          rows={1}
          dir="auto"
          // Chars, as a paste-bomb guard only; the real limit is 2048 BYTES,
          // enforced at send with a visible refusal.
          maxLength={2048}
          // SHORT placeholders on the disabled states: the full explanation
          // lives in the banner above; a long sentence in a one-row textarea
          // clips mid-word once the attach/emoji/send buttons take their width.
          placeholder={t(
            !canSend
              ? 'chat.observer'
              : composerDisabled
                ? snap.myChatMuted
                  ? 'chat.phMuted'
                  : 'chat.phFrozen'
                : 'chat.placeholder',
          )}
          disabled={composerDisabled}
          onPaste={onPaste}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              submit()
            }
          }}
          onInput={(event) => {
            const element = event.currentTarget
            element.style.height = 'auto'
            element.style.height = `${Math.min(element.scrollHeight, 88)}px`
          }}
        />
        <button
          type="button"
          className="chatpanel__send"
          onClick={submit}
          disabled={composerDisabled}
          aria-label={t('chat.send')}
          title={t('chat.send')}
        >
          <SendIcon />
        </button>
      </footer>

      {snap.refusal === 'tooLong' || snap.refusal === 'tooFast' ? (
        <p className="chatpanel__hint" role="status">
          {t(snap.refusal === 'tooLong' ? 'chat.tooLong' : 'chat.tooFast')}
        </p>
      ) : null}
      {snap.moderationFailed ? (
        <p className="chatpanel__hint" role="status">
          {t('moderate.failed')}
        </p>
      ) : null}
      {snap.imageRefusal ? (
        <p className="chatpanel__hint" role="status">
          {t(ATTACHMENT_REFUSAL_KEY[snap.imageRefusal] ?? 'chat.imgFailed')}
        </p>
      ) : null}
      {localNotice ? (
        <p className="chatpanel__hint" role="status">
          {localNotice}
        </p>
      ) : null}
      {snap.pending > 0 ? (
        <p className="chatpanel__hint" role="status">
          {t('chat.sending').replace('{count}', String(snap.pending))}
        </p>
      ) : null}

      {lightbox ? <Lightbox src={lightbox} onClose={() => setLightbox(null)} /> : null}

      {emojiOpen ? (
        <EmojiGrid
          onPick={(emoji) => {
            const input = inputRef.current
            if (!input) return
            const start = input.selectionStart ?? input.value.length
            const end = input.selectionEnd ?? input.value.length
            input.value = input.value.slice(0, start) + emoji + input.value.slice(end)
            const caret = start + emoji.length
            input.setSelectionRange(caret, caret)
            input.focus()
          }}
          onClose={() => setEmojiOpen(false)}
        />
      ) : null}
    </div>
  )
}

/**
 * Memoized: a commit that appends two messages re-renders two bubbles, not the
 * whole window. Safe because the store replaces row objects on every change
 * (tombstoning included) — reference equality IS content equality here.
 */
const Bubble = memo(function Bubble({
  message,
  mine,
  showName,
  canModerate,
  muted,
  onDelete,
  onToggleMute,
  onOpenImage,
  onOpenFile,
  onRetryImage,
  unfurl,
}: {
  message: ChatMessage
  mine: boolean
  showName: boolean
  canModerate: boolean
  muted: boolean
  onDelete: (id: string) => void
  onToggleMute: (identity: string, muted: boolean) => void
  onOpenImage: (url: string) => void
  onOpenFile: (id: string) => void
  onRetryImage: (id: string) => void
  unfurl: (url: string) => Promise<LinkPreview | null>
}) {
  const teacher = isTeacher(message.identity)
  const attachment = !message.deleted ? message.attachment : null
  // LK-009: files are their own shape — the public-S3 URL math below is
  // image-only (a file object is private; it opens through a presigned GET).
  const fileAttachment = attachment != null && isFileAttachment(attachment) ? attachment : null
  const imageAttachment = attachment != null && !isFileAttachment(attachment) ? attachment : null
  // Only hosts can delete, so a host viewing a tombstone is (in the
  // single-tutor common case) its author: "Removed by you", not
  // "Removed by your teacher". Students keep the teacher phrasing.
  const removedKey = canModerate ? 'chat.removedByYou' : 'chat.removed'
  // The full image URL: the local preview while uploading (already decoded in
  // memory — no flicker to a network fetch), else the public S3 object.
  const fullUrl = message.localPreview ?? (imageAttachment?.key ? attachmentUrl(imageAttachment.key) : null)
  const thumbUrl = message.localPreview
    ?? (imageAttachment?.thumb_key ? attachmentUrl(imageAttachment.thumb_key) : fullUrl)
  return (
    <div className={`bubble ${mine ? 'bubble--mine' : ''} ${teacher ? 'bubble--teacher' : ''}`}>
      {showName && !mine ? (
        <span className="bubble__name">
          <bdi dir="auto">{message.name}</bdi>
          {teacher ? <span className="badge badge--teacher">{t('role.teacher')}</span> : null}
        </span>
      ) : null}
      <div className="bubble__row">
        <div className={`bubble__body ${imageAttachment ? 'bubble__body--image' : ''}`} dir="auto">
          {message.deleted ? (
            <span className="bubble__removed">{t(removedKey)}</span>
          ) : fileAttachment ? (
            <FileAttachment
              id={message.id}
              filename={fileAttachment.filename}
              size={fileAttachment.size}
              uploadState={message.uploadState}
              onOpen={onOpenFile}
              onRetry={onRetryImage}
            />
          ) : imageAttachment && thumbUrl ? (
            <>
              <ImageAttachment
                id={message.id}
                thumbUrl={thumbUrl}
                fullUrl={fullUrl}
                attachment={imageAttachment}
                uploadState={message.uploadState}
                onOpen={onOpenImage}
                onRetry={onRetryImage}
              />
              {/* A caption. The wire and the server have always carried body and
                  attachment together; only the client insisted a picture had
                  nothing to say. "Look at question 3" is the whole message. */}
              {message.body ? (
                <span className="bubble__caption">
                  <MessageBody body={message.body} />
                </span>
              ) : null}
            </>
          ) : (
            <MessageBody body={message.body} />
          )}
        </div>
        {/* Visible state, not just a highlighted icon: the tutor must be able
            to SEE who is chat-muted at a glance. Host-only, so a muted child
            is never labelled in front of classmates. */}
        {canModerate && muted && !message.deleted ? (
          <span className="bubble__mutedchip">{t('chat.mutedChip')}</span>
        ) : null}
        {canModerate && !message.deleted ? (
          <span className="bubble__tools">
            <button
              type="button"
              className="bubble__tool"
              onClick={() => onDelete(message.id)}
              aria-label={t('chat.delete')}
              title={t('chat.delete')}
            >
              <TrashIcon />
            </button>
            {!mine && (message.identity.startsWith('Student:') || message.identity.startsWith('Parent:')) ? (
              <button
                type="button"
                className={`bubble__tool ${muted ? 'bubble__tool--active' : ''}`}
                onClick={() => onToggleMute(message.identity, muted)}
                aria-label={t(muted ? 'chat.unmuteUser' : 'chat.muteUser')}
                title={t(muted ? 'chat.unmuteUser' : 'chat.muteUser')}
              >
                {/* A SPEECH-BUBBLE slash, deliberately not a microphone: this
                    button silences their chat, and the mic glyph it used to
                    carry read as muting their voice. */}
                <ChatOffIcon />
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      {/* Preview cards and rich media are TEACHER-ONLY by policy: both render
          content with no click, and every viewer's browser hotlinks the asset.
          Both are fine for a trusted adult's link and neither is fine for
          whatever a student pastes. Student links stay clickable (MessageBody
          above); they just never auto-render anything.

          A direct media link is handled BEFORE the unfurl card, and instead of
          it: the server has no OpenGraph tags to find on a bare `.gif`, so that
          path returns null and the tutor's link would render as nothing. */}
      {!message.deleted && teacher ? (
        <TeacherRichContent body={message.body} unfurl={unfurl} onOpenImage={onOpenImage} />
      ) : null}
      <span className="bubble__time">{formatTime(message.at)}</span>
    </div>
  )
})

/**
 * Full-size view. Tap-to-zoom rather than pinch handling: a tutor on a laptop
 * has no pinch, and a worked example photographed at arm's length is unreadable
 * at fit-to-screen on any device. Zoomed, the image pans by drag.
 *
 * The close button is a real button with a real handler. It used to rely on the
 * click bubbling to the backdrop, which worked with a mouse and left keyboard
 * users pressing Enter on a control that did nothing.
 */
function Lightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const [zoomed, setZoomed] = useState(false)
  return (
    <div
      className={`chatpanel__lightbox ${zoomed ? 'chatpanel__lightbox--zoom' : ''}`}
      role="dialog"
      aria-label={t('chat.imageView')}
      onClick={onClose}
    >
      <img
        src={src}
        alt=""
        className={`chatpanel__lightbox-img ${zoomed ? 'chatpanel__lightbox-img--zoom' : ''}`}
        // The image swallows its own clicks so zooming does not also dismiss.
        onClick={(event) => {
          event.stopPropagation()
          setZoomed((value) => !value)
        }}
      />
      <button
        type="button"
        className="chatpanel__lightbox-close"
        onClick={(event) => {
          event.stopPropagation()
          onClose()
        }}
        aria-label={t('roster.close')}
        title={t('roster.close')}
      >
        <CloseIcon />
      </button>
    </div>
  )
}

/**
 * Message text with URLs promoted to real anchors. Tokens come from the pure
 * linkifier — user content is never parsed as HTML, so text stays text. The
 * link label sits in its own <bdi dir="ltr">: inside an Arabic sentence the
 * URL must read left-to-right without reordering the words around it.
 */
function MessageBody({ body }: { body: string | null }) {
  if (!body) return null
  const tokens = splitLinks(body)
  return (
    <bdi dir="auto">
      {tokens.map((token, index) =>
        token.type === 'link' && token.href ? (
          <a
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="bubble__link"
            href={token.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <bdi dir="ltr">{token.value}</bdi>
          </a>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{token.value}</span>
        ),
      )}
    </bdi>
  )
}

/**
 * What a tutor's link becomes.
 *
 * A direct media URL is answered here and the server is never asked: an unfurl
 * of a bare `.gif` finds no OpenGraph tags, returns null, and renders nothing —
 * so the link that most obviously WANTS to be shown was the one that never was.
 *
 * The split between the two kinds is not editorial, it is the CSP:
 *
 *   an image URL   → `<img>`, because `img-src` permits any https origin, and
 *                    a GIF, an animated WebP and an APNG all animate by
 *                    themselves. This is the whole of "play a GIF in chat".
 *   a video URL    → a card that OPENS OUT, because `media-src 'self' blob:`
 *                    refuses a remote `<video>` and there is no `frame-src`, so
 *                    an embed is refused too. A play button that silently did
 *                    nothing would be worse than one that admits where it goes.
 *
 * See src/chat/media.ts for the policy in full.
 */
function TeacherRichContent({
  body,
  unfurl,
  onOpenImage,
}: {
  body: string | null
  unfurl: (url: string) => Promise<LinkPreview | null>
  onOpenImage: (url: string) => void
}) {
  const url = firstLink(body)
  const media = classifyMedia(url)
  if (media) return <MediaCard media={media} onOpenImage={onOpenImage} />
  return <BubbleLinkCard body={body} unfurl={unfurl} />
}

function MediaCard({
  media,
  onOpenImage,
}: {
  media: LinkedMedia
  onOpenImage: (url: string) => void
}) {
  if (media.kind === 'image') {
    return (
      <button
        type="button"
        className="bubble__media"
        onClick={() => onOpenImage(media.url)}
        aria-label={t('chat.imageView')}
      >
        {/* Nothing to reserve from: unlike an upload, a linked image carries no
            dimensions until it has loaded. The CSS pins the box between a min
            and a max so the load cannot reflow the conversation. */}
        <img src={media.url} alt="" loading="lazy" decoding="async" draggable={false} />
      </button>
    )
  }
  return (
    <a
      className="bubble__videocard"
      href={media.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <span className="bubble__videoposter">
        {media.poster ? (
          <img src={media.poster} alt="" loading="lazy" decoding="async" />
        ) : null}
        <span className="bubble__playmark" aria-hidden="true">
          <PlayIcon />
        </span>
      </span>
      <span className="bubble__videotext">
        <span className="bubble__videotitle">
          {t('chat.playOn').replace('{site}', media.provider || t('chat.opensOutside'))}
        </span>
        {/* The tutor's real path to playing a video TO the room is the screen
            share, which already carries tab audio. Saying so here is the
            difference between a limitation and a dead end. */}
        <span className="bubble__videohint">{t('chat.shareToPlay')}</span>
      </span>
    </a>
  )
}

/**
 * The small rich card under a teacher's message. Fetch is fire-once (the
 * store memoizes per URL, negative results included) and never blocks the
 * bubble: text renders immediately, the card appears if and when the server
 * had something. No retry, no spinner, no error state — on a Cairo connection
 * a card that did not come is a card the lesson lives without.
 */
function BubbleLinkCard({
  body,
  unfurl,
}: {
  body: string | null
  unfurl: (url: string) => Promise<LinkPreview | null>
}) {
  const url = firstLink(body)
  const [preview, setPreview] = useState<LinkPreview | null>(null)

  useEffect(() => {
    if (!url) return
    let live = true
    void unfurl(url).then((result) => {
      if (live) setPreview(result)
    })
    return () => {
      live = false
    }
  }, [url, unfurl])

  if (!url || !preview) return null
  const image = isSafePreviewImage(preview.image) ? preview.image : null
  return (
    <a
      className="linkcard"
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      aria-label={preview.title}
    >
      {image ? (
        <img className="linkcard__thumb" src={image} alt="" loading="lazy" decoding="async" />
      ) : null}
      <span className="linkcard__text">
        <span className="linkcard__title">
          <bdi dir="auto">{preview.title}</bdi>
        </span>
        {preview.description ? (
          <span className="linkcard__desc">
            <bdi dir="auto">{preview.description}</bdi>
          </span>
        ) : null}
        <span className="linkcard__site">
          <bdi dir="ltr">{preview.site || new URL(url).hostname}</bdi>
        </span>
      </span>
    </a>
  )
}

/**
 * An image in a bubble: a fixed-aspect thumbnail that opens full-size on tap.
 *
 * The box reserves its exact height from the known w/h BEFORE the pixels load,
 * so an image arriving never shifts the messages above it (zero layout shift on
 * a phone mid-scroll). Capped so a portrait photo cannot take over the sheet.
 * `loading=lazy` + `decoding=async` keep it off the critical path; the decode
 * budget is the ≤12-visible window the panel already renders.
 */
const THUMB_MAX = 200
function ImageAttachment({
  id,
  thumbUrl,
  fullUrl,
  attachment,
  uploadState,
  onOpen,
  onRetry,
}: {
  id: string
  thumbUrl: string
  fullUrl: string | null
  attachment: { w: number; h: number; name?: string | null }
  uploadState?: 'uploading' | 'failed'
  onOpen: (url: string) => void
  onRetry: (id: string) => void
}) {
  const w = attachment.w || THUMB_MAX
  const h = attachment.h || THUMB_MAX
  const scale = Math.min(1, THUMB_MAX / Math.max(w, h))
  const boxW = Math.round(w * scale)
  const boxH = Math.round(h * scale)
  const uploading = uploadState === 'uploading'
  const failed = uploadState === 'failed'
  // A failed bubble is tappable to RETRY; an in-flight one is inert; a settled
  // one opens the full image. Never disabled when failed — that is the retry.
  const openable = !uploading && !failed && !!fullUrl
  return (
    <button
      type="button"
      className={`bubble__image ${uploadState ? `bubble__image--${uploadState}` : ''}`}
      style={{ width: boxW, height: boxH }}
      onClick={() => {
        if (failed) onRetry(id)
        else if (openable && fullUrl) onOpen(fullUrl)
      }}
      disabled={uploading || (!failed && !openable)}
      aria-label={failed ? t('chat.imgFailed') : attachment.name || t('chat.imageView')}
    >
      <img
        src={thumbUrl}
        alt={attachment.name || ''}
        width={boxW}
        height={boxH}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
      {uploading ? <span className="bubble__image-spin" aria-hidden="true" /> : null}
      {failed ? <span className="bubble__image-failed">{t('chat.imgFailed')}</span> : null}
    </button>
  )
}

/**
 * A small curated grid, native emoji only. Fixed logical order — RTL flips the
 * layout, and that is fine; a "recent" row that reorders per render is not.
 */
const EMOJI = [
  '😀', '😂', '🥳', '😍', '🤩', '😎', '🤔', '😅',
  '👍', '👏', '🙌', '💪', '🤝', '✋', '👀', '💡',
  '❤️', '⭐', '✨', '🎉', '🏆', '🚀', '🔥', '🌟',
  '✅', '❓', '➕', '💯', '📚', '✏️', '🧠', '🎯',
] as const

const RECENT_KEY = 'ischool-meet:recent-emoji'

function EmojiGrid({ onPick, onClose }: { onPick: (emoji: string) => void; onClose: () => void }) {
  const [recent] = useState<string[]>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[]
      return raw.filter((emoji) => typeof emoji === 'string').slice(0, 8)
    } catch {
      return []
    }
  })

  const pick = (emoji: string) => {
    onPick(emoji)
    try {
      const next = [emoji, ...recent.filter((existing) => existing !== emoji)].slice(0, 8)
      localStorage.setItem(RECENT_KEY, JSON.stringify(next))
    } catch {
      /* recents just will not persist */
    }
    onClose()
  }

  return (
    <div className="chatpanel__emoji" role="group" aria-label={t('chat.emoji')}>
      {recent.length > 0 ? (
        <div className="chatpanel__emoji-row">
          {recent.map((emoji) => (
            <button key={`r-${emoji}`} type="button" className="chatpanel__emoji-item" onClick={() => pick(emoji)}>
              {emoji}
            </button>
          ))}
        </div>
      ) : null}
      <div className="chatpanel__emoji-grid">
        {EMOJI.map((emoji) => (
          <button key={emoji} type="button" className="chatpanel__emoji-item" onClick={() => pick(emoji)}>
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * One formatter for the whole panel, not one construction per bubble per
 * render. `toLocaleTimeString` builds a formatter every call; over 40 rows on
 * every open that is the single most expensive thing in the list. (Codex
 * measured ~13× on a bulk probe.)
 */
let timeFormatter: Intl.DateTimeFormat | null = null
function formatTime(at: number): string {
  try {
    if (!timeFormatter) {
      timeFormatter = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' })
    }
    return timeFormatter.format(at)
  } catch {
    return ''
  }
}

/**
 * A non-image attachment in a bubble (LK-009): a file-type chip — icon,
 * filename, size — that opens the object through the API's short-lived
 * private presigned GET on tap. Uploading shows the shared spinner; a failed
 * PUT is the retry, exactly like images (same id, same raw bytes).
 */
function FileAttachment({
  id,
  filename,
  size,
  uploadState,
  onOpen,
  onRetry,
}: {
  id: string
  filename: string
  size: number
  uploadState?: 'uploading' | 'failed'
  onOpen: (id: string) => void
  onRetry: (id: string) => void
}) {
  const uploading = uploadState === 'uploading'
  const failed = uploadState === 'failed'
  return (
    <button
      type="button"
      className={`bubble__file ${uploadState ? `bubble__file--${uploadState}` : ''}`}
      onClick={() => (failed ? onRetry(id) : !uploading && onOpen(id))}
      disabled={uploading}
      aria-label={`${filename} · ${formatBytes(size)} · ${t(failed ? 'chat.fileFailed' : 'chat.fileOpen')}`}
    >
      <span className="bubble__file-icon" aria-hidden="true"><FileIcon /></span>
      <span className="bubble__file-text">
        <span className="bubble__file-name">
          <bdi dir="auto">{filename}</bdi>
        </span>
        <span className="bubble__file-size" dir="ltr">{formatBytes(size)} · PDF</span>
      </span>
      {uploading ? <span className="bubble__image-spin" aria-hidden="true" /> : null}
      {failed ? <span className="bubble__file-failed">{t('chat.fileFailed')}</span> : null}
    </button>
  )
}

const FileIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm7 1.5V9h5.5L13 3.5ZM8.5 12.5h7v1.4h-7zm0 3h7v1.4h-7z" />
  </svg>
)

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm0 14 4-5 3 4 3-3 4 4V5H5v12Z" />
    <circle cx="9" cy="9" r="1.6" />
  </svg>
)

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
    <path d="M10 4a6 6 0 1 1-4.2 10.3l-3.1 3.1-1.4-1.4 3.1-3.1A6 6 0 0 1 10 4Zm0 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
  </svg>
)
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M8 5.1v13.8L19 12 8 5.1Z" />
  </svg>
)
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 5.7 18.3l-1.4-1.4L10.6 12 4.3 5.7l1.4-1.4L12 10.6l4.9-4.9 1.4 1.4Z" />
  </svg>
)
const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M3 20v-6l8-2-8-2V4l19 8-19 8Z" />
  </svg>
)
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M7 21q-.8 0-1.4-.6T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .8-.6 1.4T17 21H7Zm2-4h2V8H9v9Zm4 0h2V8h-2v9Z" />
  </svg>
)
/** Speech bubble with a slash — "their chat is off", NOT a microphone. */
const ChatOffIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <path
      fill="currentColor"
      opacity="0.55"
      d="M19 4a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-5.6L9 20.4V16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h13Z"
    />
    <path fill="currentColor" d="M3.6 2.3 2.3 3.6l18.1 18.1 1.3-1.3L3.6 2.3Z" />
  </svg>
)
